/**
 * RunScoreHistory — Execution Group listing under Results showing DQ scores.
 * Route: /results/run-score-history
 *
 * Uses the paginated run instance API which already returns
 * `aggregated_run_score` and `aggregated_run_score_calculated_on` per run.
 */
import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
} from "@mui/material";
import {
  GridColDef,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
} from "@mui/x-data-grid-pro";
import { useNavigate, useSearchParams } from "react-router-dom";
import FlexBetween from "../../components/FlexBetween";
import DataTable from "../../components/DataGrids/DataGrid";
import DashboardIcon from "@mui/icons-material/DashboardOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import useFetchPaginatedRunInstance from "../../hooks/useFetchPaginatedRunInstance";
import { generateAggregatedRunScore } from "../../services/scoringService";
import { getFormattedDateTime } from "../../services/utils";
import { useRowsPerPage } from "../../hooks/useRowsPerPage";
import { useToast } from "../../services/utils";
import Loader from "../../components/Molecules/Loader/Loader";
import LimitChractersWithTooltip from "../../common/utils/LimitChractersWithTooltip";
import SearchFilterBar from "../../components/Molecules/FilterSearch/SearchFilterBar";
import useDebounce from "../../hooks/useDebounce";
import ScoreBadge from "../../components/Scoring/ScoreBadge";
import {
  IconCheckCircleIconGreenSvg,
  IconCrossCircleIconSvg,
} from "../../common/utils/icons";

const RUN_SCORE_FILTER_CONFIG = {
  DEFAULT_STATE: { run_name: "", status_filter: "Active" },
  get URL_FIELDS() {
    return Object.keys(this.DEFAULT_STATE);
  },
  FIELD_TYPES: {
    ID_FIELDS: [] as string[],
    STRING_FIELDS: ["run_name", "status_filter"],
    DATE_FIELDS: [] as string[],
  },
} as const;

const RunScoreHistory: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  const defaultPage = process.env.REACT_APP_DEFAULT_PAGE;
  const { rowsPerPage } = useRowsPerPage();
  const [page, setPage] = useState<number>(
    defaultPage ? parseInt(defaultPage) : 1,
  );
  const [pSize, setPSize] = useState<number>(rowsPerPage);
  const [isLoading, setIsLoading] = useState(false);
  const [fileData, setFileData] = useState<any>([]);
  const [generatingRunId, setGeneratingRunId] = useState<number | null>(null);

  const [searchFilterData, setSearchFilterData] = useState<any>(() => {
    const s: any = { ...RUN_SCORE_FILTER_CONFIG.DEFAULT_STATE };
    RUN_SCORE_FILTER_CONFIG.URL_FIELDS.forEach((f: string) => {
      const v = searchParams.get(f);
      if (v) s[f] = v;
    });
    return s;
  });

  const effectiveStatusFilter = searchFilterData?.status_filter || "";
  const debouncedRunName = useDebounce(searchFilterData?.run_name || "");
  const effectiveRunNameSearch =
    debouncedRunName.trim().length >= 3 ? debouncedRunName : "";

  const [fetchedRunInstance, fetchData] = useFetchPaginatedRunInstance({
    setIsLoading,
    page,
    pSize,
    searchTerm: effectiveRunNameSearch,
    statusFilter: effectiveStatusFilter,
  });

  // Sync filters → URL
  useEffect(() => {
    const p = new URLSearchParams();
    RUN_SCORE_FILTER_CONFIG.URL_FIELDS.forEach((f) => {
      const v = searchFilterData[f];
      if (v && !(f === "status_filter" && v === "Active")) p.set(f, String(v));
    });
    if (searchParams.toString() !== p.toString())
      setSearchParams(p, { replace: true });
  }, [searchFilterData, searchParams, setSearchParams]);

  // URL → state (back/forward navigation)
  useEffect(() => {
    const s: any = { ...RUN_SCORE_FILTER_CONFIG.DEFAULT_STATE };
    RUN_SCORE_FILTER_CONFIG.URL_FIELDS.forEach((f: string) => {
      const v = searchParams.get(f);
      if (v) s[f] = v;
    });
    if (
      RUN_SCORE_FILTER_CONFIG.URL_FIELDS.some(
        (f) => s[f] !== searchFilterData[f],
      )
    )
      setSearchFilterData(s);
  }, [searchParams]);

  useEffect(() => {
    setPage(1);
  }, [searchFilterData]);

  useEffect(() => {
    if (fetchedRunInstance) setFileData(fetchedRunInstance);
  }, [fetchedRunInstance]);

  const handleGenerateScore = async (runId: number) => {
    setGeneratingRunId(runId);
    try {
      await generateAggregatedRunScore(runId);
      showToast("Score generated successfully", "success");
      if (typeof fetchData === "function") fetchData();
    } catch (err: any) {
      showToast(
        err?.response?.data?.detail || "Failed to generate score",
        "error",
      );
    } finally {
      setGeneratingRunId(null);
    }
  };

  const columns: GridColDef[] = [
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      width: 1,
      minWidth: 20,
      flex: 0,
      renderCell: () => <></>,
      cellClassName: "no-padding-cell",
      headerClassName: "no-padding-cell",
    },
    {
      field: "run_id",
      headerName: "Execution Group ID",
      minWidth: 80,
      flex: 0.5,
      renderCell: (params: any) => <span>{params.id}</span>,
    },
    {
      field: "run_name",
      headerName: "Execution Group Name",
      minWidth: 180,
      flex: 1.5,
      renderCell: (params: any) => (
        <LimitChractersWithTooltip value={params?.value} />
      ),
    },
    {
      field: "aggregated_run_score",
      headerName: "DQ Score",
      minWidth: 120,
      flex: 0.8,
      align: "center",
      headerAlign: "center",
      renderCell: (params: any) => {
        const score = params.value;
        if (score === null || score === undefined) {
          return (
            <Chip
              label="Not Scored"
              size="small"
              variant="outlined"
              sx={{ color: "text.secondary", fontSize: "0.7rem" }}
            />
          );
        }
        return <ScoreBadge score={score} size="small" />;
      },
    },
    {
      field: "aggregated_run_score_calculated_on",
      headerName: "Score Calculated On",
      minWidth: 170,
      flex: 1,
      renderCell: (params: any) => {
        if (!params.value) return <span>—</span>;
        return <span>{getFormattedDateTime(params.value)}</span>;
      },
    },
    {
      field: "is_active",
      headerName: "Status",
      width: 80,
      align: "center",
      headerAlign: "center",
      renderCell: (params: any) =>
        params.value ? (
          <Tooltip title="Active" placement="top" arrow>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconCheckCircleIconGreenSvg width={21} height={21} />
            </Box>
          </Tooltip>
        ) : (
          <Tooltip title="Inactive" placement="top" arrow>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconCrossCircleIconSvg width={21} height={21} />
            </Box>
          </Tooltip>
        ),
    },
    {
      field: "action",
      headerName: "Actions",
      align: "center",
      headerAlign: "center",
      width: 200,
      renderCell: (params: any) => {
        const hasScore =
          params.row.aggregated_run_score !== null &&
          params.row.aggregated_run_score !== undefined;
        const isGenerating = generatingRunId === params.row.id;
        return (
          <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
            {hasScore && (
              <Tooltip title="View Score Dashboard" placement="top" arrow>
                <IconButton
                  className="datagrid-action-btn"
                  color="inherit"
                  onClick={() =>
                    navigate(`/results/run-score-dashboard/${params.row.id}`)
                  }
                >
                  <DashboardIcon fontSize="small" sx={{ color: "#1976d2" }} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip
              title={hasScore ? "Recalculate Score" : "Generate Score"}
              placement="top"
              arrow
            >
              <span>
                <IconButton
                  className="datagrid-action-btn"
                  color="inherit"
                  onClick={() => handleGenerateScore(params.row.id)}
                  disabled={isGenerating}
                >
                  {isGenerating ? (
                    <CircularProgress size={18} />
                  ) : (
                    <RefreshIcon
                      fontSize="small"
                      sx={{ color: hasScore ? "#ed6c02" : "#2e7d32" }}
                    />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  return (
    <Box>
      {isLoading && <Loader isLoading={isLoading} />}
      <Box className="text-box-card list-page-card compact-text-box-card">
        <Grid container rowSpacing={2.5} columnSpacing={2}>
          <Grid item xs>
            <SearchFilterBar
              largeWidth={true}
              setSearchFilterData={setSearchFilterData}
              searchFilterData={searchFilterData}
              requiredFields={["run_name", "status_filter"]}
              primaryFields={["run_name", "status_filter"]}
              fieldRequirements={{ defaultStatusFilter: "Active" }}
            />
          </Grid>
        </Grid>
      </Box>
      <FlexBetween>
        <DataTable
          dataColumns={columns}
          dataRows={fileData?.items || []}
          loading={isLoading}
          dataListTitle="Run Score History"
          className="dataTable no-radius hide-progress-icon"
          paginationMode="server"
          rowCount={fileData?.total}
          paginationModel={{ page: page - 1, pageSize: pSize }}
          onPaginationModelChange={(params: any) => {
            if (params.pageSize !== pSize || params.page !== page - 1) {
              setPage(params.page + 1);
              setPSize(params.pageSize);
            }
          }}
          singlePageMaxHeightDiff={368}
        />
      </FlexBetween>
    </Box>
  );
};

export default RunScoreHistory;
